'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Stack } from '@ironhack/design-system2/components/stack';
import { Tag } from '@ironhack/design-system2/components/tag';
import { Text } from '@ironhack/design-system2/components/text';
import { ChevronLeft } from '@ironhack/design-system2/icons/chevron-left';
import NextLink from 'next/link';
import { invertObj, pick, pipe } from 'ramda';
import { type ReactElement } from 'react';
import { Image } from 'react-datocms';

import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { type CoursePages } from '@/app/l/[region]/[language]/course/[course]/course.urls';
import { getPageUrl } from '@/app/urls/url-mapping';
import { usePageContext } from '@/hooks';
import { datoCmsIdMap } from '@/lib/datocms';
import {
  type DatoArticle,
  type DatoBlogArticleHeaderModule,
} from '@/lib/datocms/types';
import { updateBlogHomeRegion } from '@/lib/utils/blog';

type Props = DatoArticle & DatoBlogArticleHeaderModule;

export const Header = (props: Props): ReactElement => {
  const {
    articlesByText,
    author,
    backButtonText,
    categories,
    coverImage,
    publicationDate,
    readingTime,
    series,
    subtitle,
    title,
  } = props;
  const { params } = usePageContext();
  const blogHomeParams = updateBlogHomeRegion(params);

  const datoIdToCategory = pipe(
    pick(['cy', 'da', 'ux', 'wd']),
    invertObj
  )(datoCmsIdMap.categories);

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        justify: 'space-between',
        direction: 'column',
      }}
      moduleFormat={{
        spacingBottom: ['32', null, '64'],
        spacingTop: '0',
        background: 'transparent',
      }}
      outerProps={{ as: 'header' }}
    >
      <Button
        _hover={{
          color: 'link.inverse',
          textDecoration: 'underline',
        }}
        alignSelf="start"
        as={NextLink}
        color="link.inverse"
        fontSize="s"
        href={getPageUrl('blog', blogHomeParams) ?? '#/'}
        leftIcon={ChevronLeft}
        size="s"
        variant="tertiary"
      >
        {backButtonText}
      </Button>
      <Flex mb={[4, null, 8]} mt={[3, null, 6]}>
        <Box color="text.inverse" mr={[0, null, 4]}>
          {series && <Tag colorScheme="pink" text={series.name} />}
          <Text mt={2} textStyle="m">
            {publicationDate}
            {readingTime ? ` - ${readingTime}` : ''}
          </Text>
          <Text as="h1" mt={2} noOfLines={3} textStyle={['3xl', null, '5xl']}>
            {title}
          </Text>
          <Text mt={2} noOfLines={3} textStyle={['xl', null, '2xl']}>
            {subtitle}
          </Text>
          <Box display={['block', null, 'none']} mt={2} w="full">
            {coverImage && <Image data={coverImage.responsiveImage} />}
          </Box>
          <Stack
            align="center"
            bg="background.white"
            borderRadius={8}
            direction="row"
            justifyContent="flex-start"
            mt={[2, null, 4]}
            px={1.5}
            py={0.5}
            width={['full', null, 'fit-content']}
          >
            {author.photo && (
              <Image
                data={author.photo.responsiveImage}
                layout="fixed"
                objectFit="contain"
              />
            )}
            <Flex
              alignItems={['flex-start', 'center']}
              flexDir={['column', 'row']}
              ml={1.5}
              w="full"
            >
              <Box>
                <Text color="text.primary" textStyle="m">
                  {author.name}
                </Text>
                <Text
                  color="text.primary"
                  noOfLines={2}
                  textStyle="xs"
                  textTransform="uppercase"
                >
                  {author.jobTitle ? `${author.jobTitle}` : ''}
                </Text>
              </Box>
              {author.slug && (
                <Button
                  as={NextLink}
                  borderRadius="base"
                  fontSize={['s', null, 'm']}
                  fontWeight="normal"
                  href={getPageUrl('blogAuthor', {
                    ...params,
                    author: author.slug,
                  })}
                  ml={[0, 'auto', null, 5]}
                  mt={[0.5, 0]}
                  px={1}
                  py={[0.25, null, 0.5]}
                  size="s"
                >
                  {`${articlesByText} ${author.name.split(' ')[0]}`}
                </Button>
              )}
            </Flex>
          </Stack>
          <Stack direction="row" mt={[2, null, 3]} spacing={1}>
            {categories &&
              categories.map(({ id, name }) => {
                const courseCategory = id && datoIdToCategory[id];
                let pageUrl;
                if (courseCategory) {
                  pageUrl = getPageUrl(
                    `course/${courseCategory}` as CoursePages,
                    params
                  );
                }

                const tagToRender = pageUrl ? (
                  <NextLink href={pageUrl} key={name}>
                    <Tag colorScheme="dark" text={name} />
                  </NextLink>
                ) : (
                  <Tag colorScheme="dark" key={name} text={name} />
                );

                return tagToRender;
              })}
          </Stack>
        </Box>
        <Box display={['none', null, 'block']} minW="35rem">
          {coverImage && <Image data={coverImage.responsiveImage} />}
        </Box>
      </Flex>
    </ModuleWrapper>
  );
};
